<template>
  <teleport to="body">
    <transition name="modal" appear>
      <div v-if="visible" class="bert-modal-overlay" @click="handleOverlayClick">
        <div class="bert-modal-container" :class="modalClasses" @click.stop>
          <div class="bert-modal-header" v-if="$slots.header || title || closable">
            <slot name="header">
              <h3 v-if="title" class="bert-modal-title">{{ title }}</h3>
            </slot>
            <button v-if="closable" @click="close" class="bert-modal-close">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>
          
          <div class="bert-modal-body">
            <slot></slot>
          </div>
          
          <div v-if="$slots.footer" class="bert-modal-footer">
            <slot name="footer"></slot>
          </div>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script>
export default {
  name: 'BertModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ''
    },
    size: {
      type: String,
      default: 'md',
      validator: (value) => ['sm', 'md', 'lg', 'xl', 'full'].includes(value)
    },
    closable: {
      type: Boolean,
      default: true
    },
    closeOnOverlay: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    modalClasses() {
      return [
        'bert-modal',
        `bert-modal--${this.size}`
      ]
    }
  },
  methods: {
    close() {
      this.$emit('close')
    },
    handleOverlayClick() {
      if (this.closeOnOverlay) {
        this.close()
      }
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = ''
      }
    }
  },
  beforeUnmount() {
    document.body.style.overflow = ''
  }
}
</script>

<style scoped>
.bert-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-modal);
  padding: var(--space-4);
}

.bert-modal-container {
  background-color: white;
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-2xl);
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* Sizes */
.bert-modal--sm {
  width: 100%;
  max-width: 400px;
}

.bert-modal--md {
  width: 100%;
  max-width: 500px;
}

.bert-modal--lg {
  width: 100%;
  max-width: 700px;
}

.bert-modal--xl {
  width: 100%;
  max-width: 900px;
}

.bert-modal--full {
  width: 95vw;
  height: 95vh;
  max-width: none;
  max-height: none;
}

.bert-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-6);
  border-bottom: 1px solid var(--neutral-200);
  background-color: var(--neutral-50);
}

.bert-modal-title {
  margin: 0;
  font-size: var(--text-lg);
  font-weight: var(--font-semibold);
  color: var(--neutral-800);
}

.bert-modal-close {
  background: none;
  border: none;
  cursor: pointer;
  padding: var(--space-2);
  border-radius: var(--radius-base);
  color: var(--neutral-500);
  transition: all var(--transition-base);
  display: flex;
  align-items: center;
  justify-content: center;
}

.bert-modal-close:hover {
  background-color: var(--neutral-100);
  color: var(--neutral-700);
}

.bert-modal-body {
  padding: var(--space-6);
  overflow-y: auto;
  flex: 1;
}

.bert-modal-footer {
  padding: var(--space-6);
  border-top: 1px solid var(--neutral-200);
  background-color: var(--neutral-50);
  display: flex;
  gap: var(--space-3);
  justify-content: flex-end;
}

/* Transitions */
.modal-enter-active,
.modal-leave-active {
  transition: opacity var(--transition-base);
}

.modal-enter-active .bert-modal-container,
.modal-leave-active .bert-modal-container {
  transition: transform var(--transition-base);
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .bert-modal-container,
.modal-leave-to .bert-modal-container {
  transform: scale(0.95) translateY(-20px);
}
</style>